/**
 * Carte de contrôle X̄-R.
 *
 * Deux graphiques liés : la carte de position au-dessus, la carte de dispersion en
 * dessous. Le zoom et le survol sont synchronisés — un ingénieur qualité regarde
 * toujours les deux ensemble, un point suspect sur la moyenne s'explique souvent par
 * l'étendue.
 *
 * Choix d'affichage qui comptent :
 *
 * - **Renderer Canvas**, pas SVG. Sur 50 000 points, SVG crée autant de nœuds DOM et
 *   l'onglet se fige. Canvas tient la charge, ce qui est la contrainte réelle : une
 *   caractéristique surveillée depuis deux ans en accumule des dizaines de milliers.
 * - **Zones A/B/C en aplats**, pas en lignes. Elles servent de repère de lecture, pas
 *   de données ; en lignes elles concurrenceraient visuellement les points.
 * - **Points en violation en rouge et plus gros.** Le mode Atelier est lu à trois
 *   mètres d'un écran de bord de ligne : la couleur seule ne suffit pas.
 */

import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { useMemo } from "react";

import type { CharacteristicView } from "./api";
import { useChartPalette } from "./ui/theme";

interface Props {
  data: CharacteristicView;
  height?: number;
}

export function ControlChart({ data, height = 520 }: Props) {
  const palette = useChartPalette();

  // Un canvas ne connaît ni les variables CSS ni la classe `dark` : il faut lui donner
  // des couleurs littérales. Les lire sur le thème plutôt que les figer ici évite que
  // les graphiques restent en mode clair quand l'interface bascule.
  const COLORS = useMemo(
    () => ({
      point: palette.accent,
      violation: palette.bad,
      center: palette.good,
      limit: palette.bad,
      spec: palette.warn,
      zoneA: `${palette.bad}14`,
      zoneB: `${palette.warn}14`,
      zoneC: `${palette.good}0d`,
    }),
    [palette],
  );

  const option = useMemo<EChartsOption>(() => {
    const loc = data.location_limits;
    const disp = data.dispersion_limits;

    // Indices des sous-groupes concernés par au moins une violation.
    const flagged = new Set<number>();
    for (const violation of data.violations) {
      for (const index of violation.indices) flagged.add(index);
    }

    // Un libellé par point en violation, pour le tooltip.
    const reasons = new Map<number, string[]>();
    for (const violation of data.violations) {
      for (const index of violation.indices) {
        const list = reasons.get(index) ?? [];
        list.push(`${violation.severity} — ${violation.label}`);
        reasons.set(index, list);
      }
    }

    const labels = data.timestamps.map((ts) =>
      new Date(ts).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    );

    const locationPoints = data.location_points.map((value, index) => ({
      value,
      itemStyle: flagged.has(index) ? { color: COLORS.violation } : undefined,
      symbolSize: flagged.has(index) ? 11 : 6,
    }));

    // Alignement des abscisses, qui dépend du type de carte :
    //
    //   X̄-R et X̄-S : une étendue (ou un écart-type) par sous-groupe, alignement 1 pour 1.
    //   I-MR        : l'étendue mobile compte un point de moins, la première mesure
    //                 n'ayant pas de prédécesseur. Il faut alors décaler d'un cran.
    //
    // Décaler dans les deux cas — l'erreur du premier jet — désaligne silencieusement
    // toute la carte des étendues : chaque point est attribué au sous-groupe suivant.
    const needsOffset = data.chart_type === "I-MR";
    const dispersionPoints: (number | null)[] = needsOffset
      ? [null, ...data.dispersion_points]
      : [...data.dispersion_points];

    const line = (
      value: number,
      color: string,
      label: string,
      dashed = false,
    ) => ({
      yAxis: value,
      lineStyle: {
        color,
        type: dashed ? ("dashed" as const) : ("solid" as const),
        width: 1.5,
      },
      label: {
        formatter: label,
        position: "insideEndTop" as const,
        color,
        fontSize: 11,
      },
    });

    return {
      animation: false,
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        formatter: (params: unknown) => {
          const items = params as Array<{
            dataIndex: number;
            seriesName: string;
            value: number;
          }>;
          const first = items[0];
          if (!first) return "";
          const index = first.dataIndex;
          const rows = items
            .filter((p) => p.value !== null && p.value !== undefined)
            .map(
              (p) =>
                `${p.seriesName} : <b>${Number(p.value).toFixed(4)} ${data.unit}</b>`,
            )
            .join("<br/>");
          const why = reasons.get(index);
          const alert = why
            ? `<div style="margin-top:6px;color:${COLORS.violation}">⚠ ${why.join("<br/>⚠ ")}</div>`
            : "";
          return `<b>${labels[index] ?? ""}</b><br/>${rows}${alert}`;
        },
      },
      axisPointer: { link: [{ xAxisIndex: "all" }] },
      grid: [
        { left: 70, right: 30, top: 40, height: height * 0.45 },
        { left: 70, right: 30, top: height * 0.45 + 90, height: height * 0.25 },
      ],
      xAxis: [
        {
          type: "category",
          data: labels,
          gridIndex: 0,
          axisLabel: { show: false },
        },
        {
          type: "category",
          data: labels,
          gridIndex: 1,
          axisLabel: {
            fontSize: 10,
            rotate: 45,
            interval: Math.ceil(labels.length / 12),
          },
        },
      ],
      yAxis: [
        {
          type: "value",
          gridIndex: 0,
          name: `Moyenne (${data.unit})`,
          nameLocation: "middle",
          nameGap: 50,
          scale: true,
        },
        {
          type: "value",
          gridIndex: 1,
          name: "Étendue",
          nameLocation: "middle",
          nameGap: 50,
          scale: true,
        },
      ],
      dataZoom: [
        { type: "inside", xAxisIndex: [0, 1] },
        { type: "slider", xAxisIndex: [0, 1], bottom: 8, height: 18 },
      ],
      series: [
        {
          name: "Moyenne du sous-groupe",
          type: "line",
          data: locationPoints,
          xAxisIndex: 0,
          yAxisIndex: 0,
          lineStyle: { color: COLORS.point, width: 1.5 },
          itemStyle: { color: COLORS.point },
          symbol: "circle",
          markArea: {
            silent: true,
            data: [
              [
                { yAxis: loc.zone_high_2, itemStyle: { color: COLORS.zoneA } },
                { yAxis: loc.upper },
              ],
              [
                { yAxis: loc.lower, itemStyle: { color: COLORS.zoneA } },
                { yAxis: loc.zone_low_2 },
              ],
              [
                { yAxis: loc.zone_high_1, itemStyle: { color: COLORS.zoneB } },
                { yAxis: loc.zone_high_2 },
              ],
              [
                { yAxis: loc.zone_low_2, itemStyle: { color: COLORS.zoneB } },
                { yAxis: loc.zone_low_1 },
              ],
              [
                { yAxis: loc.zone_low_1, itemStyle: { color: COLORS.zoneC } },
                { yAxis: loc.zone_high_1 },
              ],
            ],
          },
          markLine: {
            silent: true,
            symbol: "none",
            data: [
              line(loc.upper, COLORS.limit, "LCS"),
              line(loc.center, COLORS.center, "Moyenne"),
              line(loc.lower, COLORS.limit, "LCI"),
              line(data.specification.usl, COLORS.spec, "USL", true),
              line(data.specification.lsl, COLORS.spec, "LSL", true),
            ],
          },
        },
        {
          name: "Étendue du sous-groupe",
          type: "line",
          data: dispersionPoints,
          xAxisIndex: 1,
          yAxisIndex: 1,
          lineStyle: { color: palette.muted, width: 1.5 },
          itemStyle: { color: palette.muted },
          symbol: "circle",
          symbolSize: 5,
          markLine: {
            silent: true,
            symbol: "none",
            data: [
              line(disp.upper, COLORS.limit, "LCS"),
              line(disp.center, COLORS.center, "R̄"),
            ],
          },
        },
      ],
    };
  }, [data, height, COLORS, palette]);

  return (
    <ReactECharts
      option={option}
      style={{ height, width: "100%" }}
      // Canvas plutôt que SVG : indispensable au-delà de quelques milliers de points.
      opts={{ renderer: "canvas" }}
      notMerge
    />
  );
}
